import React, { useContext, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useAuthContext } from 'state/auth';
import { useDayPartContext } from 'state/day-part';
import { useLoggerContext } from 'state/logger';
import { useMParticleContext } from 'state/mParticle';
import { useNetworkContext } from 'state/network';
import { useServiceModeContext } from 'state/service-mode';

import useOffers from './hooks/use-offers';

export { RedemptionMethod, Status } from './hooks/use-offer-redemption';

export type OffersValue = Omit<ReturnType<typeof useOffers>, 'OfferUnavailableDialog'>;

export const OffersContext = React.createContext<OffersValue>({} as OffersValue);
export const useOffersContext = () => useContext(OffersContext);

export const OffersProvider: React.FC<IBaseProps> = props => {
  const { user } = useAuthContext();
  const { activeDayParts } = useDayPartContext();
  const { decorateLogger, logger } = useLoggerContext();
  const mParticle = useMParticleContext();
  const { query } = useNetworkContext();
  const { serviceMode } = useServiceModeContext();
  const { formatMessage } = useIntl();

  const {
    OfferUnavailableDialog,
    clearSelectedOffer,
    isSelectedOfferCartEntry,
    loading,
    offers,
    offersFeedback,
    offerValidationErrors,
    lockedOffers,
    onCancelRedemption,
    onConfirmRedemption,
    onSelectMobileRedemption,
    onSelectRestaurantRedemption,
    openOfferUnavailableDialog,
    promoCodeOffers,
    togglePromoCodeOfferRedeemable,
    redemptionMethod,
    selectOfferById,
    selectedOffer,
    selectedOfferCartEntry,
    selectedOfferPrice,
    setOfferValidationErrors,
    setRedemptionMethod,
    setSelectedOfferCartEntry,
    setSelectedOfferPrice,
    queryRedeemableOffers,
    validateOfferRedeemable,
    refreshOffers,
    activateOffer,
    validateCouponRedeemable
  } = useOffers({
    activeDayParts,
    decorateLogger,
    logger,
    mParticle,
    query,
    serviceMode,
    user
  });

  const context = useMemo(
    () => ({
      clearSelectedOffer,
      isSelectedOfferCartEntry,
      loading,
      offers,
      offersFeedback,
      offerValidationErrors,
      lockedOffers,
      onCancelRedemption,
      onConfirmRedemption,
      onSelectMobileRedemption,
      onSelectRestaurantRedemption,
      openOfferUnavailableDialog,
      promoCodeOffers,
      togglePromoCodeOfferRedeemable,
      redemptionMethod,
      selectOfferById,
      selectedOffer,
      selectedOfferCartEntry,
      selectedOfferPrice,
      setOfferValidationErrors,
      setRedemptionMethod,
      setSelectedOfferCartEntry,
      setSelectedOfferPrice,
      queryRedeemableOffers,
      validateOfferRedeemable,
      refreshOffers,
      activateOffer,
      validateCouponRedeemable
    }),
    [
      clearSelectedOffer,
      isSelectedOfferCartEntry,
      loading,
      offers,
      offersFeedback,
      offerValidationErrors,
      lockedOffers,
      onCancelRedemption,
      onConfirmRedemption,
      onSelectMobileRedemption,
      onSelectRestaurantRedemption,
      openOfferUnavailableDialog,
      promoCodeOffers,
      togglePromoCodeOfferRedeemable,
      redemptionMethod,
      selectOfferById,
      selectedOffer,
      selectedOfferCartEntry,
      selectedOfferPrice,
      setOfferValidationErrors,
      setRedemptionMethod,
      setSelectedOfferCartEntry,
      setSelectedOfferPrice,
      queryRedeemableOffers,
      validateOfferRedeemable,
      refreshOffers,
      activateOffer,
      validateCouponRedeemable
    ]
  );

  return (
    <OffersContext.Provider value={context}>
      {props.children}
      <OfferUnavailableDialog
        body={formatMessage(messages.couponErrorBody)}
        confirmLabel={formatMessage(messages.couponErrorConfirm)}
        heading={formatMessage(messages.couponErrorHeading)}
      />
    </OffersContext.Provider>
  );
};

export default OffersContext.Consumer;
const messages = defineMessages({
  couponErrorHeading: {
    id: 'couponErrorHeading',
    defaultMessage: 'Coupon Error',
    description: 'No description'
  },

  couponErrorConfirm: {
    id: 'couponErrorConfirm',
    defaultMessage: 'Complete Order',
    description: 'No description'
  },

  couponErrorBody: {
    id: 'couponErrorBody',
    defaultMessage:
      "Your order contains a coupon that's no longer available. You can still complete your order but we'll remove the coupon from the cart.",
    description: 'No description'
  }
});
